import { useEffect, useRef, useState } from 'react';
import * as matchStorage from '../storage/matchStorage';

/**
 * Court of a paired tablet, as handed over by the pairing page (pair.php). Same fields as
 * the QR setup payload of scan-court.php, so the tablet can also score a manual match here.
 */
export interface PairedCourtInfo {
  context: string;
  centerId: string;
  centerName: string;
  courtId: string;
  courtName: string;
  liveScoreUrl: string;
  scoringType: string;
  resultCallbackUrl: string;
}

/** What the tablet keeps in localStorage once paired. Survives clearAll(). */
export interface PairedCourt {
  deviceToken: string;
  deviceId: string;
  label: string;
  /** Livescore API directory, e.g. https://ref.my.squash.ch/livescore/api */
  apiBase: string;
  court: PairedCourtInfo;
  pairedAt: number;
}

/** Battery of the tablet as sent to next-match.php: a whole percentage and the charger state. */
export interface DeviceBattery {
  level: number;
  charging: boolean;
}

/** The fields of the browser's BatteryManager the poll reads. */
interface BatteryManagerLike {
  level: number;
  charging: boolean;
}

/** Health of the polling link, shown on the idle screen. */
export type PairingLink = 'idle' | 'online' | 'offline';

interface UseCourtPairingOptions {
  /** True while the tablet is on its waiting screen and may take a match. */
  idle: boolean;
  /** Called with the launch payload of the match the backend handed over. */
  onMatchReceived: (match: any) => void;
  /** Called when the center manager asked the tablet to drop its match (reset-device.php). */
  onReset?: () => void;
}

interface UseCourtPairingReturn {
  pairedCourt: PairedCourt | null;
  link: PairingLink;
}

/** Poll interval on the waiting screen: how fast a match sent to the court opens. */
export const PAIRING_POLL_INTERVAL_MS = 5000;
/** Poll interval while a match is loaded: presence only, under the backend's 60 s online window. */
export const PRESENCE_POLL_INTERVAL_MS = 30000;
const REQUEST_TIMEOUT_MS = 8000;
const FRAGMENT_PREFIX = '#pair=';

/** API directory of a livescore endpoint URL: .../api/update-score.php -> .../api */
export function apiBaseOf(endpointUrl: string): string {
  return endpointUrl.replace(/\/[^/]*$/, '');
}

/**
 * Pairing handed over by pair.php in the URL fragment: `#pair=<base64url JSON>` carrying
 * `{ deviceToken, device: { id, label }, court }`. Returns null when the fragment is absent
 * or unusable (a truncated link, a hand-edited URL): the app then simply is not paired.
 */
export function parsePairingFragment(hash: string, now: number = Date.now()): PairedCourt | null {
  if (!hash.startsWith(FRAGMENT_PREFIX)) return null;
  try {
    const base64 = hash.slice(FRAGMENT_PREFIX.length).replace(/-/g, '+').replace(/_/g, '/');
    const json = new TextDecoder().decode(Uint8Array.from(atob(base64), c => c.charCodeAt(0)));
    const data = JSON.parse(json);
    if (typeof data?.deviceToken !== 'string' || typeof data?.device?.id !== 'string'
      || typeof data?.court?.liveScoreUrl !== 'string') {
      return null;
    }
    return {
      deviceToken: data.deviceToken,
      deviceId: data.device.id,
      label: typeof data.device.label === 'string' ? data.device.label : '',
      apiBase: apiBaseOf(data.court.liveScoreUrl),
      court: data.court,
      pairedAt: now,
    };
  } catch (error) {
    console.warn('[Pairing] Unusable pairing fragment:', error);
    return null;
  }
}

/**
 * Reads the pairing fragment once, at start: stores the pairing (it replaces an earlier one)
 * and removes the fragment from the URL, so the token never lingers in the address bar.
 *
 * A pairing starts the tablet afresh: any match stored on the device is dropped. It may belong
 * to another court (the tablet moved) and would broadcast there, or be a stale match the
 * tablet would reopen instead of its waiting screen. Runs in the useState initializer, during
 * the first render, so before the effects that read the stored match (useMatchData,
 * useMatchRecovery).
 */
function takePairingFromUrl(): PairedCourt | null {
  if (!window.location.hash.startsWith(FRAGMENT_PREFIX)) return null;
  const paired = parsePairingFragment(window.location.hash);
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  if (paired) {
    matchStorage.clearAll();
    matchStorage.savePairedCourt(paired);
  }
  return paired;
}

/**
 * BatteryManager of the browser (Battery Status API), or null when it has none: only Chromium
 * browsers (Chrome, Edge, Samsung Internet) have one; Safari, so every browser on iOS/iPadOS,
 * and Firefox do not. The manager is live, so it is asked for once and read at each poll.
 */
function getBatteryManager(): Promise<BatteryManagerLike | null> {
  const nav = navigator as Navigator & { getBattery?: () => Promise<BatteryManagerLike> };
  if (typeof nav.getBattery !== 'function') return Promise.resolve(null);
  return nav.getBattery().catch(() => null);
}

/** Battery as sent to the backend, rounded to the percent, or null when unknown. */
export function batteryOf(manager: BatteryManagerLike | null): DeviceBattery | null {
  if (!manager || typeof manager.level !== 'number' || !Number.isFinite(manager.level)) return null;
  return { level: Math.round(manager.level * 100), charging: Boolean(manager.charging) };
}

async function postJson(url: string, body: unknown): Promise<{ status: number; body: any }> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    const data = await res.json().catch(() => null);
    return { status: res.status, body: data };
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Pairing of this tablet with a court (backend side: docs/technical/livescore-api.md, court pairing).
 *
 * - The pairing QR code of the center manager opens the backend pairing page (pair.php).
 *   Once the organizer confirms, the page redirects here with the device token and the
 *   court in the URL fragment; they are kept in localStorage (`pairedCourt`), and any match
 *   stored on the device is dropped. The app offers
 *   no pairing or unpairing of its own: both are the organizer's gestures.
 * - As long as it is paired, the tablet polls next-match.php (paused while the tab is hidden),
 *   telling the backend what it does, which the center manager shows:
 *   - on its waiting screen (`idle`), every 5 s with `activity: "idle"`: the backend hands the
 *     match waiting for the court over, passed to `onMatchReceived`; the caller then stores
 *     it and lets the usual data flow show it;
 *   - with a match loaded, every 30 s with `activity: "match"`: presence only, the backend
 *     keeps any match sent meanwhile until the tablet is back on its waiting screen, which
 *     triggers a poll at once.
 *   Each poll also carries the battery of the tablet (`battery: { level, charging }`) when the
 *   browser tells it, shown by the center manager; the very first poll goes without it rather
 *   than wait for the browser.
 * - A match the backend handed over is passed on even when the tablet left its waiting screen
 *   while the request was travelling: the backend has already deleted it.
 * - A 401 means the center manager unpaired this tablet: the pairing is dropped silently,
 *   without touching a match in progress.
 * - `command: "reset"` in a response means the center manager asked the tablet to drop what it
 *   holds (a stuck result screen, a wrong match): passed to `onReset`, the tablet stays paired.
 */
export function useCourtPairing({ idle, onMatchReceived, onReset }: UseCourtPairingOptions): UseCourtPairingReturn {
  const [pairedCourt, setPairedCourt] = useState<PairedCourt | null>(
    () => takePairingFromUrl() ?? matchStorage.readPairedCourt()
  );
  const [link, setLink] = useState<PairingLink>('idle');

  const onMatchReceivedRef = useRef(onMatchReceived);
  onMatchReceivedRef.current = onMatchReceived;
  const onResetRef = useRef(onReset);
  onResetRef.current = onReset;
  const idleRef = useRef(idle);
  idleRef.current = idle;
  const pollNowRef = useRef<() => void>(() => {});

  // One polling loop for as long as the tablet is paired; its pace and the declared activity
  // follow `idle` at each request, so leaving the waiting screen never cancels a request.
  useEffect(() => {
    if (!pairedCourt) {
      setLink('idle');
      return;
    }

    let stopped = false;
    let inFlight = false;
    // A poll asked for while one is travelling (the tablet just changed screens) runs right after it
    let pollAgain = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let batteryManager: BatteryManagerLike | null = null;
    getBatteryManager().then(manager => { batteryManager = manager; });
    const { apiBase, deviceToken } = pairedCourt;

    const scheduleNext = () => {
      clearTimeout(timer);
      if (stopped) return;
      timer = setTimeout(poll, idleRef.current ? PAIRING_POLL_INTERVAL_MS : PRESENCE_POLL_INTERVAL_MS);
    };

    const poll = async () => {
      if (stopped || inFlight) return;
      if (document.hidden) {
        scheduleNext();
        return;
      }
      inFlight = true;
      try {
        const activity = idleRef.current ? 'idle' : 'match';
        const battery = batteryOf(batteryManager);
        const res = await postJson(`${apiBase}/next-match.php`, { deviceToken, activity, ...(battery && { battery }) });
        if (res.status === 401) {
          if (!stopped) {
            matchStorage.clearPairedCourt();
            setPairedCourt(null);
          }
          return;
        }
        if (res.status !== 200 || !res.body?.success) {
          if (!stopped) setLink('offline');
          return;
        }
        if (!stopped) setLink('online');
        if (res.body.command === 'reset') {
          onResetRef.current?.();
        } else if (res.body.match) {
          onMatchReceivedRef.current(res.body.match);
        }
      } catch (error) {
        if (!stopped) setLink('offline');
      } finally {
        inFlight = false;
        if (pollAgain && !stopped) {
          pollAgain = false;
          poll();
        } else {
          scheduleNext();
        }
      }
    };

    pollNowRef.current = () => {
      if (inFlight) {
        pollAgain = true;
        return;
      }
      clearTimeout(timer);
      poll();
    };
    poll();
    const onVisible = () => { if (!document.hidden) pollNowRef.current(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      stopped = true;
      clearTimeout(timer);
      pollNowRef.current = () => {};
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [pairedCourt]);

  // Back on the waiting screen: ask for a match at once rather than at the next presence poll
  const wasIdleRef = useRef(idle);
  useEffect(() => {
    if (idle && !wasIdleRef.current) pollNowRef.current();
    wasIdleRef.current = idle;
  }, [idle]);

  return { pairedCourt, link };
}
